import React, { useEffect, useState } from 'react';
import { ArrowLeftIcon, LinkedinIcon, TwitterIcon, MailIcon, BookOpenIcon, UserIcon, CheckCircleIcon, UsersIcon, TrendingUpIcon, GemIcon, AwardIcon } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
// Sample data for connections
const connectionsData = [{
  id: 1,
  name: 'Marco Rossi',
  role: 'Data Scientist',
  company: 'Intel',
  location: 'Milano, Italia',
  avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
  tags: ['AI', 'Machine Learning', 'ESG'],
  bio: "Esperto di intelligenza artificiale con focus su soluzioni sostenibili. Sviluppo algoritmi di machine learning per ottimizzare l'efficienza energetica e ridurre l'impatto ambientale.",
  groups: ['AI for Sustainability', 'Green Tech Network'],
  courses: [{
    name: 'Climate Change 101: cosa dice la scienza',
    completed: true,
    date: 'Maggio 2023'
  }],
  stats: {
    weeklyImpact: '78%',
    diamonds: '1,240',
    rewards: '12'
  }
}];
// Default profile data (Alex Rossi)
const defaultProfile = {
  id: 0,
  name: 'Alex Rossi',
  role: 'Ricercatore PhD',
  company: 'Università di Milano',
  location: 'Milano, Italia',
  avatar: 'https://cdn.magicpatterns.com/uploads/wAuXNp8Njv5DkmF4gEUeC7/jonathan-cooper-ViyA5myhBVw-unsplash.jpg',
  tags: ['Climate Science', 'Energia Rinnovabile', 'Ricerca Ambientale', 'Divulgazione'],
  bio: 'Ricercatore in scienze ambientali con focus su cambiamento climatico e transizione energetica. Mi dedico alla ricerca scientifica e alla divulgazione di soluzioni pratiche per la sostenibilità. Il mio obiettivo è rendere la scienza del clima accessibile e trasformarla in azioni concrete per un futuro più sostenibile.',
  groups: ['Climate Research Network', 'Ricercatori per il Clima', 'Divulgazione Scientifica Italia'],
  courses: [{
    name: 'Climate Change 101: cosa dice la scienza',
    completed: true,
    date: 'Dicembre 2023'
  }, {
    name: 'Casa efficiente: energia, bollette e comfort',
    completed: true,
    date: 'Novembre 2023'
  }, {
    name: 'Mobilità sostenibile: muoversi meglio, spendere meno',
    completed: false,
    progress: 45
  }],
  stats: {
    weeklyImpact: '94%',
    diamonds: '2,850',
    rewards: '23'
  }
};
const PublicProfile = () => {
  const {
    userId
  } = useParams();
  const [profileData, setProfileData] = useState(defaultProfile);
  useEffect(() => {
    // If userId is provided, find the matching connection
    if (userId) {
      const connection = connectionsData.find(c => c.id === parseInt(userId));
      if (connection) {
        setProfileData({
          ...connection,
          stats: connection.stats || {
            weeklyImpact: '0%',
            diamonds: '0',
            rewards: '0'
          }
        });
      }
    }
  }, [userId]);
  return <div className="min-h-screen bg-gray-50" data-id="element-3020">
      {/* Hero section with gradient background */}
      <div className="bg-gradient-to-br from-green-600 to-emerald-700 text-white" data-id="element-3021">
        {/* Topbar */}
        <div className="container mx-auto px-4 py-4 flex justify-between items-center" data-id="element-3022">
          <img src="https://cdn.magicpatterns.com/uploads/w4Sqqi6gRHWGegGAQT2PTG/Greenback_logo_(1).png" alt="Greenback Logo" className="h-8 object-contain" data-id="element-3023" />
          <Link to="/" className="py-2 px-4 bg-white text-green-600 rounded-lg font-medium hover:bg-gray-50 transition-colors text-sm" data-id="element-3024">
            Torna alla Dashboard
          </Link>
        </div>

        {/* Profile Header */}
        <div className="container mx-auto px-4 pb-16 pt-8" data-id="element-3025">
          <div className="flex flex-col items-center" data-id="element-3026">
            {/* Profile image */}
            <div className="w-32 h-32 rounded-full border-4 border-white overflow-hidden mb-6 shadow-xl" data-id="element-3027">
              <img src={profileData.avatar} alt={profileData.name} className="w-full h-full object-cover" data-id="element-3028" />
            </div>

            {/* Profile info */}
            <h1 className="text-3xl font-semibold mb-2 text-center text-white" data-id="element-3029">
              {profileData.name}
            </h1>
            <p className="text-xl text-white/90 mb-2 text-center font-normal" data-id="element-3030">
              {profileData.role}
            </p>
            <p className="text-lg text-white/80 mb-4 text-center font-normal" data-id="element-3031">
              {profileData.company && `${profileData.company} • `}
              {profileData.location.split(',')[0]}
            </p>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 justify-center mb-6 max-w-2xl" data-id="element-3032">
              {profileData.tags.map((tag, index) => <span key={index} className="px-3 py-1 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-normal" data-id="element-3033">
                  {tag}
                </span>)}
            </div>

            {/* Social links */}
            <div className="flex gap-3 mt-4" data-id="element-3034">
              <button className="p-2 bg-white/10 backdrop-blur-sm rounded-full text-white hover:bg-white/20 transition-colors" data-id="element-3035">
                <LinkedinIcon size={20} strokeWidth={1.5} data-id="element-3036" />
              </button>
              <button className="p-2 bg-white/10 backdrop-blur-sm rounded-full text-white hover:bg-white/20 transition-colors" data-id="element-3037">
                <TwitterIcon size={20} strokeWidth={1.5} data-id="element-3038" />
              </button>
              <button className="p-2 bg-white/10 backdrop-blur-sm rounded-full text-white hover:bg-white/20 transition-colors" data-id="element-3039">
                <MailIcon size={20} strokeWidth={1.5} data-id="element-3040" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Stats Section - SIMPLIFIED */}
      <div className="bg-white border-b border-gray-200 shadow-sm" data-id="element-3041">
        <div className="container mx-auto px-4 py-6" data-id="element-3042">
          <div className="max-w-6xl mx-auto grid grid-cols-3 divide-x divide-gray-200" data-id="element-3043">
            <KpiCard icon={<TrendingUpIcon size={18} strokeWidth={1.5} data-id="element-3045" />} value={profileData.stats.weeklyImpact} label="Impatto Settimanale" trend="Azioni, challenge ed eventi" trendUp={true} data-id="element-3044" />
            <KpiCard icon={<GemIcon size={18} strokeWidth={1.5} data-id="element-3047" />} value={profileData.stats.diamonds} label="Diamond Ottenuti" trend="+180 questo mese" trendUp={true} data-id="element-3046" />
            <KpiCard icon={<AwardIcon size={18} strokeWidth={1.5} data-id="element-3049" />} value={profileData.stats.rewards} label="Premi Riscattati" trend="Ultimi 6 mesi" trendUp={true} data-id="element-3048" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8" data-id="element-3050">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-3051">
          {/* Left Column - About & Groups */}
          <div className="md:col-span-2 space-y-6" data-id="element-3052">
            {/* About Section */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100" data-id="element-3053">
              <div className="flex items-center mb-4" data-id="element-3054">
                <UserIcon className="text-green-600 mr-2" size={20} strokeWidth={1.5} data-id="element-3055" />
                <h2 className="text-xl font-semibold text-gray-800" data-id="element-3056">
                  Chi sono
                </h2>
              </div>
              <p className="text-gray-700 leading-relaxed font-normal" data-id="element-3057">
                {profileData.bio}
              </p>
            </div>

            {/* Active Groups */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100" data-id="element-3058">
              <div className="flex items-center mb-4" data-id="element-3059">
                <UsersIcon className="text-green-600 mr-2" size={20} strokeWidth={1.5} data-id="element-3060" />
                <h2 className="text-xl font-semibold text-gray-800" data-id="element-3061">
                  Gruppi attivi
                </h2>
              </div>
              <div className="space-y-3" data-id="element-3062">
                {profileData.groups.map((group, index) => <GroupItem key={index} name={group} members={Math.floor(Math.random() * 30 + 20)} data-id="element-3063" />)}
              </div>
            </div>
          </div>

          {/* Right Column - Courses */}
          <div className="space-y-6" data-id="element-3064">
            {/* Greenback Path */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100" data-id="element-3065">
              <div className="flex items-center mb-4" data-id="element-3066">
                <BookOpenIcon className="text-green-600 mr-2" size={20} strokeWidth={1.5} data-id="element-3067" />
                <h2 className="text-xl font-semibold text-gray-800" data-id="element-3068">
                  Percorso Formativo
                </h2>
              </div>
              <div className="space-y-4" data-id="element-3069">
                {profileData.courses.map((course, index) => <div key={index} className="flex items-start" data-id="element-3070">
                    <div className="w-16 h-16 rounded-md overflow-hidden flex-shrink-0 bg-gray-100" data-id="element-3071">
                      <img src={course.name.includes('Climate') ? 'https://www.innovationnewsnetwork.com/wp-content/uploads/2020/03/image-1.jpg' : course.name.includes('Casa') ? 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80' : 'https://images.unsplash.com/photo-1571068316344-75bc76f77890?w=800&q=80'} alt={course.name} className="w-full h-full object-cover" data-id="element-3072" />
                    </div>
                    <div className="ml-3 flex-1" data-id="element-3073">
                      <div className="flex items-center" data-id="element-3074">
                        {course.completed ? <CheckCircleIcon size={16} className="text-green-600 mr-1" strokeWidth={2} data-id="element-3075" /> : <div className="w-4 h-4 rounded-full border-2 border-gray-300 mr-1" data-id="element-3076"></div>}
                        <h3 className="font-medium text-gray-800 text-sm" data-id="element-3077">
                          {course.name}
                        </h3>
                      </div>
                      <p className="text-xs text-gray-500 mt-1 font-normal" data-id="element-3078">
                        {course.completed ? `Completato • ${course.date}` : `In corso • ${course.progress}% completato`}
                      </p>
                      {!course.completed && <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2" data-id="element-3079">
                          <div className="bg-green-600 h-1.5 rounded-full transition-all duration-500" style={{
                      width: `${course.progress}%`
                    }} data-id="element-3080"></div>
                        </div>}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-8 mt-10" data-id="element-3081">
        <div className="container mx-auto px-4 text-center" data-id="element-3082">
          <img src="https://cdn.magicpatterns.com/uploads/w4Sqqi6gRHWGegGAQT2PTG/Greenback_logo_(1).png" alt="Greenback Logo" className="h-10 w-auto object-contain mx-auto mb-4" data-id="element-3083" />
          <p className="text-gray-600 text-sm font-normal" data-id="element-3084">
            © 2024 Greenback - Piattaforma per la Sostenibilità
          </p>
          <p className="text-gray-500 text-xs mt-2 font-normal" data-id="element-3085">
            Tutti i diritti riservati
          </p>
        </div>
      </footer>
    </div>;
};
interface KpiCardProps {
  icon: React.ReactNode;
  value: string;
  label: string;
  trend: string;
  trendUp?: boolean;
}
const KpiCard: React.FC<KpiCardProps> = ({
  icon,
  value,
  label,
  trend,
  trendUp
}) => {
  return <div className="px-4 py-5 flex flex-col items-center text-center" data-id="element-3086">
      <div className="p-2 bg-green-50 rounded-full text-green-600 mb-2" data-id="element-3087">
        {icon}
      </div>
      <div className="text-2xl font-semibold text-gray-800 mb-1" data-id="element-3088">{value}</div>
      <div className="text-sm text-gray-600 font-normal" data-id="element-3089">{label}</div>
      <div className={`text-xs ${trendUp ? 'text-green-600' : 'text-gray-500'} mt-1 font-normal`} data-id="element-3090">
        {trend}
      </div>
    </div>;
};
interface GroupItemProps {
  name: string;
  members: number;
}
const GroupItem: React.FC<GroupItemProps> = ({
  name,
  members
}) => {
  return <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-3091">
      <div className="flex items-center" data-id="element-3092">
        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600 mr-3" data-id="element-3093">
          <UsersIcon size={16} strokeWidth={1.5} data-id="element-3094" />
        </div>
        <span className="font-medium text-gray-700 text-sm" data-id="element-3095">{name}</span>
      </div>
      <span className="text-sm text-gray-500 font-normal" data-id="element-3096">
        {members} membri
      </span>
    </div>;
};
export default PublicProfile;